import { useState, useCallback, useEffect } from 'react'
import { createBrowserClient } from '@/supabase'
import { getClientEstimates } from '@/apis/estimates'
import { decideEstimate } from '@/apis/estimates'
import { toast } from '@/hooks/use-toast'
import type { Estimate, EstimateDetail } from '../estimates/types'
import { normalizeStatus } from '../utils/status'

export type ViewMode = 'project' | 'all'
export type StatusFilter = 'all' | 'decision_needed' | 'negotiating' | 'accept' | 'rejected'
export type SortBy = 'updated_at' | 'amount_asc' | 'submitted_at'
export type RequestTypeFilter = 'all' | 'request_based' | 'direct_proposal'

interface UseQuotesInboxOptions {
  onEstimateSelect?: (estimate: EstimateDetail | null) => void
}

/**
 * 견적 인박스 데이터 관리 Hook
 */
export function useQuotesInbox(options: UseQuotesInboxOptions = {}) {
  const { onEstimateSelect } = options
  const [supabase] = useState(() => createBrowserClient())
  
  const [loading, setLoading] = useState(true)
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [selectedEstimate, setSelectedEstimate] = useState<EstimateDetail | null>(null)
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null)
  
  const [viewMode, setViewMode] = useState<ViewMode>('project')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('updated_at')
  const [requestTypeFilter, setRequestTypeFilter] = useState<RequestTypeFilter>('all')
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
  
  // 읽음 상태는 DB에서 가져온 is_read 필드 사용

  // feild 배열 정규화 헬퍼
  const normalizeFeild = (v: any): string[] | null => {
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v.trim()) return [v.trim()]
    return null
  }

  /**
   * 견적서 목록 로드
   * ✅ 최적화: 초기 로딩과 백그라운드 새로고침 분리 (깜빡임 방지)
   * ✅ 최적화: N+1 쿼리 개선 (counsel, teams, accounts를 in()으로 한 번에 조회)
   */
  const loadEstimates = useCallback(async () => {
    const isInitial = estimates.length === 0
    
    try {
      // ✅ 최초만 로딩 표시, 이후는 백그라운드에서 조용히 갱신
      if (isInitial) setLoading(true)
      
      const estimateRows = await getClientEstimates(undefined, 'SUBMITTED')
      
      // ✅ N+1 쿼리 최적화: 필요한 ID들을 모아서 한 번에 조회
      const counselIds = Array.from(new Set(estimateRows.map(r => r.counsel_id)))
      const teamIds = Array.from(new Set(
        estimateRows
          .filter(r => r.proposer_type === 'TEAM' && r.proposer_team_id)
          .map(r => r.proposer_team_id!)
      ))
      
      // 병렬로 한 번에 조회
      const [counselsResult, teamsResult] = await Promise.all([
        counselIds.length > 0 
          ? supabase.from('counsel').select('*').in('counsel_id', counselIds)
          : Promise.resolve({ data: [], error: null }),
        teamIds.length > 0
          ? supabase.from('teams').select('id, name, manager_profile_id').in('id', teamIds)
          : Promise.resolve({ data: [], error: null }),
      ])
      
      const counselsMap = new Map<
        number,
        {
          counsel_id: number
          title: string | null
          outline: string | null
          counsel_status: string | null
          start_date: string | null
          cost: string | null
          period: string | null
          field: string[] | null
          created_at: string | null
        }
      >((counselsResult.data || []).map((c: any) => [Number(c.counsel_id), c]))
      const teamsMap = new Map<
        number,
        { id: number; name: string | null; manager_profile_id?: string | null }
      >((teamsResult.data || []).map((t: any) => [Number(t.id), t]))
      
      // teams의 manager_profile_id 수집
      const managerProfileIds = Array.from(new Set(
        Array.from(teamsMap.values())
          .map((t: any) => t.manager_profile_id)
          .filter((id): id is string => Boolean(id))
      ))
      const individualProfileIds = Array.from(new Set(
        estimateRows
          .filter(r => r.proposer_type === 'INDIVIDUAL' && r.proposer_profile_id)
          .map(r => r.proposer_profile_id!)
      ))
      const allProfileIds = Array.from(new Set([...managerProfileIds, ...individualProfileIds]))
      
      // accounts 한 번에 조회
      const accountsResult = allProfileIds.length > 0
        ? await supabase
            .from('accounts')
            .select('profile_id, username, contact_phone, contact_email, contact_website')
            .in('profile_id', allProfileIds)
            .is('deleted_at', null)
        : { data: [], error: null }
      
      const accountsMap = new Map((accountsResult.data || []).map((a: any) => [a.profile_id, a]))
      
      // 견적서 데이터 구성
      const estimates: Estimate[] = []
      
      for (const row of estimateRows) {
        const counsel = counselsMap.get(row.counsel_id)
        if (!counsel) {
          console.warn('counsel 정보가 없는 견적서:', row.id)
          continue
        }
        
        // 팀 또는 개인 정보 조회
        let teamInfo: Estimate['teams'] = []
        let proposerName: string | null = null
        
        if (row.proposer_type === 'TEAM' && row.proposer_team_id) {
          const team = teamsMap.get(row.proposer_team_id)
          if (team) {
            const account = (team as any).manager_profile_id 
              ? accountsMap.get((team as any).manager_profile_id)
              : null
            
            teamInfo = [{
              id: team.id,
              name: team.name,
              manager_profile_id: (team as any).manager_profile_id || null,
              contact_phone: (account as any)?.contact_phone || null,
              contact_email: (account as any)?.contact_email || null,
              contact_website: (account as any)?.contact_website || null,
            }]
          }
        } else if (row.proposer_type === 'INDIVIDUAL' && row.proposer_profile_id) {
          const account = accountsMap.get(row.proposer_profile_id)
          if (account) {
            proposerName = (account as any).username || null
            teamInfo = [{
              id: 0,
              name: null,
              manager_profile_id: row.proposer_profile_id,
              contact_phone: (account as any)?.contact_phone || null,
              contact_email: (account as any)?.contact_email || null,
              contact_website: (account as any)?.contact_website || null,
            }]
          }
        }
        
        const estimateStatus = normalizeStatus(row.status)
        
        // is_read 필드 추가 (getClientEstimates에서 반환된 데이터 사용)
        const isRead = (row as any).is_read === true
        
        const estimate: Estimate & { proposerType?: 'TEAM' | 'INDIVIDUAL', proposerName?: string | null, is_read?: boolean } = {
          estimate_id: row.id,
          team_id: row.proposer_team_id || 0,
          counsel_id: row.counsel_id,
          client_id: row.requester_client_id,
          estimate_status: estimateStatus,
          estimate_start_date: null,
          estimate_due_date: null,
          estimate_date: row.submitted_at ? row.submitted_at.split('T')[0] : null,
          total_amount: row.total_amount,
          detail: row.detail,
          created_at: row.created_at,
          updated_at: row.updated_at || row.created_at,
          request_id: row.request_id || null,
          teams: teamInfo,
          proposerType: row.proposer_type as 'TEAM' | 'INDIVIDUAL',
          proposerName: proposerName,
          is_read: isRead,
          counsel: {
            counsel_id: counsel.counsel_id,
            title: counsel.title ?? '',
            outline: counsel.outline ?? null,
            counsel_status: counsel.counsel_status ?? 'pending',
            start_date: counsel.start_date || '',
            cost: counsel.cost,
            period: counsel.period,
            field: normalizeFeild(counsel.field),
          },
        }
        
        estimates.push(estimate)
      }
      
      setEstimates(estimates)
    } catch (error: any) {
      console.error('견적서 목록 로드 실패:', error)
      toast({
        variant: 'destructive',
        title: '견적서 목록을 불러오는데 실패했습니다',
        description: error.message,
      })
    } finally {
      if (isInitial) setLoading(false)
    }
  }, [supabase, estimates.length])

  /**
   * 견적서 상세 로드
   */
  const loadEstimateDetail = useCallback(async (estimateId: number) => {
    try {
      const { data: estimateRow } = await supabase
        .from('estimates' as any)
        .select('*')
        .eq('id', estimateId)
        .maybeSingle()
      
      if (!estimateRow) {
        throw new Error('견적서 정보를 찾을 수 없습니다')
      }
      
      const row = estimateRow as any
      
      // counsel 정보 조회
      const { data: counsel } = await supabase
        .from('counsel')
        .select('*')
        .eq('counsel_id', row.counsel_id)
        .maybeSingle()
      
      if (!counsel) {
        throw new Error('프로젝트 정보를 찾을 수 없습니다')
      }
      
      // 팀 또는 개인 정보 조회
      let teamInfo: Estimate['teams'] = []
      let proposerName: string | null = null
      
      if (row.proposer_type === 'TEAM' && row.proposer_team_id) {
        const { data: team } = await supabase
          .from('teams')
          .select('id, name, manager_profile_id')
          .eq('id', row.proposer_team_id)
          .maybeSingle()
        
        if (team) {
          const { data: account } = await supabase
            .from('accounts')
            .select('contact_phone, contact_email, contact_website')
            .eq('profile_id', (team as any).manager_profile_id)
            .maybeSingle()
          
          teamInfo = [{
            id: team.id,
            name: team.name,
            manager_profile_id: (team as any).manager_profile_id || null,
            contact_phone: (account as any)?.contact_phone || null,
            contact_email: (account as any)?.contact_email || null,
            contact_website: (account as any)?.contact_website || null,
          }]
        }
      } else if (row.proposer_type === 'INDIVIDUAL' && row.proposer_profile_id) {
        // 개인 제출자 정보 조회
        const { data: account } = await supabase
          .from('accounts')
          .select('username, contact_phone, contact_email, contact_website')
          .eq('profile_id', row.proposer_profile_id)
          .is('deleted_at', null)
          .maybeSingle()
        
        if (account) {
          proposerName = (account as any).username || null
          // 개인인 경우에도 teams 배열에 연락처 정보 포함 (호환성)
          teamInfo = [{
            id: 0,
            name: null,
            manager_profile_id: row.proposer_profile_id,
            contact_phone: (account as any)?.contact_phone || null,
            contact_email: (account as any)?.contact_email || null,
            contact_website: (account as any)?.contact_website || null,
          }]
        }
      }
      
      // 마일스톤 조회
      const { data: milestones } = await supabase
        .from('milestones')
        .select('*')
        .eq('estimate_id', estimateId)
        .order('start_date', { ascending: true })
      
      // 마일스톤 데이터 매핑 (DB 필드명 → 타입 필드명)
      const mappedMilestones = milestones?.map((m: any) => ({
        milestone_id: m.id,
        estimate_id: m.estimate_id,
        title: m.title,
        detail: m.detail,
        payment_amount: m.amount,
        milestone_start_date: m.start_date,
        milestone_due_date: m.due_date,
        progress: m.progress || 0,
      })) || []
      
      const estimateStatus = normalizeStatus(row.status)
      
      const normalizedData: EstimateDetail & { proposerType?: 'TEAM' | 'INDIVIDUAL', proposerName?: string | null } = {
        estimate_id: row.id,
        team_id: row.proposer_team_id || 0,
        counsel_id: row.counsel_id,
        client_id: row.requester_client_id,
        estimate_status: estimateStatus,
        estimate_start_date: null,
        estimate_due_date: null,
        estimate_date: row.submitted_at ? row.submitted_at.split('T')[0] : null,
        total_amount: row.total_amount,
        detail: row.detail,
        created_at: row.created_at,
        updated_at: row.updated_at || row.created_at, // 최근 업데이트 날짜
        request_id: row.request_id || null, // 견적 요청 ID
        teams: teamInfo,
        proposerType: row.proposer_type as 'TEAM' | 'INDIVIDUAL',
        proposerName: proposerName,
        counsel: {
          counsel_id: counsel.counsel_id,
          title: counsel.title,
          outline: counsel.outline,
          counsel_status: counsel.counsel_status,
          start_date: counsel.start_date || '',
          cost: counsel.cost,
          period: counsel.period,
          field: normalizeFeild(counsel.field),
        },
        milestone: mappedMilestones.length > 0 ? mappedMilestones : undefined,
      }
      
      setSelectedEstimate(normalizedData)
      setSelectedEstimateId(estimateId)
      onEstimateSelect?.(normalizedData)
      
      // 읽음 처리 API 호출
      try {
        const response = await fetch(`/api/estimates/${estimateId}/read`, {
          method: 'POST',
        })
        if (response.ok) {
          // 읽음 처리 성공 시 목록의 해당 견적도 업데이트
          setEstimates(prev => prev.map(est => {
            if (est.estimate_id === estimateId) {
              return { ...est, is_read: true }
            }
            return est
          }))
        }
      } catch (error) {
        console.error('읽음 처리 실패:', error)
        // 읽음 처리 실패해도 UI는 계속 진행
      }
    } catch (error: any) {
      console.error('견적서 상세 로드 실패:', error)
      toast({
        variant: 'destructive',
        title: '견적서 상세를 불러오는데 실패했습니다',
        description: error.message,
      })
    }
  }, [supabase, onEstimateSelect]) // ✅ readEstimateIds를 deps에서 제거

  /**
   * 견적서 선택
   */
  const selectEstimate = useCallback((estimateId: number) => {
    loadEstimateDetail(estimateId)
  }, [loadEstimateDetail])

  /**
   * 견적서 상태 부분 업데이트 (전체 리로드 없이)
   */
  const updateEstimateStatus = useCallback((estimateId: number, newStatus: 'accept' | 'rejected') => {
    setEstimates(prev => prev.map(est => {
      if (est.estimate_id === estimateId) {
        return {
          ...est,
          estimate_status: newStatus,
          updated_at: new Date().toISOString(),
        }
      }
      return est
    }))
    
    // 선택된 견적도 업데이트
    if (selectedEstimate?.estimate_id === estimateId) {
      setSelectedEstimate(prev => prev ? {
        ...prev,
        estimate_status: newStatus,
        updated_at: new Date().toISOString(),
      } : null)
    }
  }, [selectedEstimate])

  /**
   * 견적서 수락
   */
  const acceptEstimate = useCallback(async (estimateId: number, shouldCloseProject?: boolean) => {
    try {
      await decideEstimate({
        estimateId,
        decision: 'ACCEPT',
        shouldCloseProject,
      })
      toast({
        title: '견적서가 수락되었습니다',
      })
      // 부분 업데이트로 즉시 UI 반영 (깜빡거림 방지)
      updateEstimateStatus(estimateId, 'accept')
      // 상세 정보만 다시 로드 (전체 목록 리로드 없이)
      if (selectedEstimate?.estimate_id === estimateId) {
        await loadEstimateDetail(estimateId)
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '견적서 수락 실패',
        description: error.message,
      })
    }
  }, [updateEstimateStatus, loadEstimateDetail, selectedEstimate])

  /**
   * 견적서 거절
   */
  const rejectEstimate = useCallback(async (estimateId: number) => {
    try {
      await decideEstimate({
        estimateId,
        decision: 'REJECT',
      })
      toast({
        title: '견적서가 거절되었습니다',
      })
      // 부분 업데이트로 즉시 UI 반영 (깜빡거림 방지)
      updateEstimateStatus(estimateId, 'rejected')
      // 상세 정보만 다시 로드 (전체 목록 리로드 없이)
      if (selectedEstimate?.estimate_id === estimateId) {
        await loadEstimateDetail(estimateId)
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '견적서 거절 실패',
        description: error.message,
      })
    }
  }, [updateEstimateStatus, loadEstimateDetail, selectedEstimate])

  /**
   * 필터링된 견적서 목록
   */
  const filteredEstimates = estimates.filter(estimate => {
    const normalizedStatus = normalizeStatus(estimate.estimate_status)
    
    // 프로젝트 필터
    if (selectedProjectId !== null) {
      if (estimate.counsel_id !== selectedProjectId) {
        return false
      }
    }
    
    // 상태 필터
    if (statusFilter !== 'all') {
      if (statusFilter === 'decision_needed') {
        // 결정 필요 = pending 상태이고 request_id가 없는 경우 (직접 제안)
        if (normalizedStatus !== 'pending' || estimate.request_id) {
          return false
        }
      } else if (statusFilter === 'negotiating') {
        // 협의중 = pending 상태이고 request_id가 있는 경우
        if (normalizedStatus !== 'pending' || !estimate.request_id) {
          return false
        }
      } else if (statusFilter === 'accept') {
        if (normalizedStatus !== 'accept') {
          return false
        }
      } else if (statusFilter === 'rejected') {
        if (normalizedStatus !== 'rejected') {
          return false
        }
      }
    }
    
    // 요청 타입 필터
    if (requestTypeFilter !== 'all') {
      if (requestTypeFilter === 'request_based' && !estimate.request_id) {
        return false
      }
      if (requestTypeFilter === 'direct_proposal' && estimate.request_id) {
        return false
      }
    }
    
    // 검색 필터 (프로젝트명/제출자명)
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const proposerName = (estimate as any).proposerName?.toLowerCase() || 
                          estimate.teams?.[0]?.name?.toLowerCase() || ''
      const counselTitle = estimate.counsel?.title?.toLowerCase() || ''
      
      if (!proposerName.includes(term) && !counselTitle.includes(term)) {
        return false
      }
    }
    
    return true
  }).sort((a, b) => {
    // 정렬
    if (sortBy === 'amount_asc') {
      const aAmount = a.total_amount || 0
      const bAmount = b.total_amount || 0
      return aAmount - bAmount // 오름차순
    } else if (sortBy === 'submitted_at') {
      const aDate = a.estimate_date || a.created_at || ''
      const bDate = b.estimate_date || b.created_at || ''
      return bDate.localeCompare(aDate) // 최신 제출일 우선
    } else {
      // updated_at (기본)
      const aDate = a.updated_at || a.created_at || ''
      const bDate = b.updated_at || b.created_at || ''
      return bDate.localeCompare(aDate) // 최신 업데이트 우선
    }
  })

  /**
   * 프로젝트별 그룹화
   */
  const projectGroups = filteredEstimates.reduce((acc, estimate) => {
    if (!estimate.counsel_id || !estimate.counsel) return acc
    
    const existing = acc.find(g => g.counsel_id === estimate.counsel_id)
    if (existing) {
      existing.estimates.push(estimate)
    } else {
      acc.push({
        counsel_id: estimate.counsel_id,
        counsel: estimate.counsel,
        estimates: [estimate],
      })
    }
    return acc
  }, [] as Array<{ counsel_id: number; counsel: Estimate['counsel']; estimates: Estimate[] }>)

  /**
   * 프로젝트 칩 데이터 (전체 견적 기준, 필터링 전)
   */
  const projectChips = estimates.reduce((acc, estimate) => {
    if (!estimate.counsel_id || !estimate.counsel) return acc
    
    const existing = acc.find(p => p.counsel_id === estimate.counsel_id)
    const normalizedStatus = normalizeStatus(estimate.estimate_status)
    const isDecisionNeeded = normalizedStatus === 'pending' && !estimate.request_id
    
    if (existing) {
      if (isDecisionNeeded) {
        existing.decisionNeededCount++
      }
    } else {
      acc.push({
        counsel_id: estimate.counsel_id,
        title: estimate.counsel.title || '제목 없음',
        decisionNeededCount: isDecisionNeeded ? 1 : 0,
      })
    }
    return acc
  }, [] as Array<{ counsel_id: number; title: string; decisionNeededCount: number }>)
  
  // 결정 필요 개수 기준으로 정렬 (많은 순), 그 다음 프로젝트명 순
  projectChips.sort((a, b) => {
    if (b.decisionNeededCount !== a.decisionNeededCount) {
      return b.decisionNeededCount - a.decisionNeededCount
    }
    return a.title.localeCompare(b.title)
  })

  useEffect(() => {
    loadEstimates()
  }, [loadEstimates])

  useEffect(() => {
    if (loading || filteredEstimates.length === 0) return

    const hasSelectedInFilter = selectedEstimateId
      ? filteredEstimates.some((estimate) => estimate.estimate_id === selectedEstimateId)
      : false

    if (!hasSelectedInFilter) {
      loadEstimateDetail(filteredEstimates[0].estimate_id)
    }
  }, [loading, filteredEstimates, selectedEstimateId, loadEstimateDetail])

  return {
    loading,
    estimates: filteredEstimates,
    projectGroups,
    projectChips,
    selectedEstimate,
    selectedEstimateId,
    selectedProjectId,
    setSelectedProjectId,
    viewMode,
    setViewMode,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    requestTypeFilter,
    setRequestTypeFilter,
    selectEstimate,
    acceptEstimate,
    rejectEstimate,
    reloadEstimates: loadEstimates,
    isRead: (estimateId: number) => {
      const estimate = estimates.find(e => e.estimate_id === estimateId)
      return (estimate as any)?.is_read === true
    },
  }
}
